import { autorun } from "mobx"
import { getRoot, Instance, SnapshotOut, types } from "mobx-state-tree"
import NetInfo from "~/ui/netinfo"
import { debug } from "../../utils"
import { RootStoreModel } from ".."
import { WebScoketClient, WebSocketState } from "./client"
import { globalSpinner } from "~utils"

// socket 连接状态
export enum SocketStates {
  IDLE = 0,
  CONNECTING,
  CONNECTED,
  DISCONNECTED,
  RECONNECTING,
  CLOSED,
}

const socketStatesText: { [key in SocketStates]: string } = {
  [SocketStates.IDLE]: "等待连接",
  [SocketStates.CONNECTING]: "连接中",
  [SocketStates.CONNECTED]: "连接成功",
  [SocketStates.DISCONNECTED]: "连接断开",
  [SocketStates.RECONNECTING]: "重连中",
  [SocketStates.CLOSED]: "关闭",
}

// const RETRY_INTERVAL = 5000 // 重试间隔时间

export const WebsocketStoreModel = types
  .model("WebsocketStore")
  .props({
    state: types.optional(types.number, SocketStates.IDLE),
  })
  .volatile(() => ({
    error: "",
    socketState: WebSocketState.CLOSED,
    online: true,
  }))
  .views((self) => ({
    get url() {
      const { configStore, authStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      return `${configStore.ws}?token=${authStore.token}`
    },
    get closed() {
      return self.state === SocketStates.CLOSED
    },
    get stateText() {
      return socketStatesText[self.state]
    },
    get opened() {
      return self.state === SocketStates.CONNECTED
    },
    get ready() {
      return self.state === SocketStates.CONNECTED
    },
    get loading() {
      return self.state === SocketStates.CONNECTING || self.state === SocketStates.RECONNECTING
    },
    hasError() {
      return self.error !== ""
    },
    get hasToken() {
      const { authStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      return authStore.hasToken
    },
  }))
  .actions((self) => ({
    setOnline(online: boolean) {
      self.online = online
    },
    setError(err: string) {
      self.error = err
    },
    clearError() {
      self.error = ""
    },
    setState(state: SocketStates) {
      self.state = state
    },
    setSocketState(state: WebSocketState) {
      self.socketState = state
    },
  }))
  .actions((self) => {
    const rootStore = getRoot<Instance<typeof RootStoreModel>>(self)
    const { channelStore, configStore } = rootStore

    let disposer
    let timerId

    let interval = 2 // 2 seconds
    let tried = 0

    function onOpen() {
      self.setState(SocketStates.CONNECTED)
      self.setSocketState(sock.state)
      self.clearError()
      // reset
      tried = 0
      interval = 2
    }

    function onData(blob: Uint8Array) {
      channelStore.ondata(blob)
    }

    function onClose(event: WebSocketCloseEvent) {
      self.setSocketState(sock.state)
      channelStore.rejectPendingOnDisconnect()
      // 上一个状态如果不是关闭状态则设置为断开状态
      if (self.state !== SocketStates.CLOSED) {
        self.setState(SocketStates.DISCONNECTED)
      }
    }

    function handlError({ code, message }: { code: string; message: string }) {
      // console.warn(`[SOCK] code:${code}, ${message}`)
      self.setError(message)
      switch (code) {
        case "zed'": // 401 android will get "zed'"
          close()
          debug("code: 401, reset all")
          if (self.hasToken) {
            globalSpinner.show("账号已在其他装置登录")
            rootStore.reset()
          }
          break
        case "401": // // 401 ios will get "401"
          close()
          debug("code: 401, reset all")
          if (self.hasToken) {
            globalSpinner.show("账号已在其他装置登录")
            rootStore.reset()
          }
          break
        default:
          configStore.setWS(
            configStore.ws_arr[Math.round(configStore.ws_arr.length * Math.random())],
          )
          self.setSocketState(sock.state)
          break
      }
    }

    function onError(e: WebSocketErrorEvent) {
      const message = (e?.message || "").trim()
      if (!message) return
      const code = message.slice(-4).trim()
      handlError({ code, message })
    }

    const sock = new WebScoketClient({
      onOpen,
      onData,
      onClose,
      onError,
    })

    function send(b: Uint8Array) {
      return sock.send(b)
    }

    function connect() {
      self.setState(SocketStates.CONNECTING)
    }

    function close() {
      self.setState(SocketStates.CLOSED)
    }

    function reconnect() {
      self.setState(SocketStates.RECONNECTING)
    }

    // 基于状态机实现断线重连
    const onStateChange = (state: SocketStates) => {
      debug(`[SOCK] ST:${SocketStates[self.state]}`)
      // socket 状态机
      switch (state) {
        case SocketStates.CONNECTING:
          sock.connect(self.url)
          break
        case SocketStates.CLOSED:
          clearInterval(timerId)
          sock.close()
          break
        case SocketStates.DISCONNECTED:
          // 断开后，触发重连机制
          reconnect()
          break
        case SocketStates.RECONNECTING:
          clearTimeout(timerId)
          // eslint-disable-next-line no-case-declarations
          const ms = Math.min(interval ** tried, 5) * 1000
          debug(`trying to connect after ${ms}ms, tried: ${tried}`)
          timerId = setTimeout(connect, ms)
          tried += 1
          break
        default:
          break
      }
    }

    function onlineCallback() {
      self.setOnline(true)
    }

    function offlineCallback() {
      self.setOnline(false)
      // globalSpinner.show("网络连接不稳定，请检查网路环境")
      if (self.state === SocketStates.CONNECTED) {
        reconnect()
      }
    }

    let unsubscribe
    let visibilityHandler: (() => void) | undefined
    function afterCreate() {
      try {
        unsubscribe = NetInfo.addEventListener((state) => {
          state.isConnected ? onlineCallback() : offlineCallback()
        })
      } catch {
        // web stub may not implement NetInfo
      }
      if (typeof document !== "undefined") {
        visibilityHandler = () => {
          if (document.visibilityState !== "visible") return
          const { authStore } = rootStore
          if (!authStore.hasToken) return
          if (self.state === SocketStates.CLOSED) return
          if (!self.opened) {
            reconnect()
          }
        }
        document.addEventListener("visibilitychange", visibilityHandler)
      }
      disposer = autorun(() => {
        onStateChange(self.state)
      })
    }

    function beforeDestroy() {
      unsubscribe && unsubscribe()
      if (visibilityHandler && typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", visibilityHandler)
      }
      close()
      disposer && disposer()
    }

    return {
      afterCreate,
      beforeDestroy,
      connect,
      reconnect,
      close,
      send,
    }
  })

type WebsocketStoreType = Instance<typeof WebsocketStoreModel>
export interface WebsocketStore extends WebsocketStoreType {}
type WebsocketStoreSnapshotType = SnapshotOut<typeof WebsocketStoreModel>
export interface WebsocketStoreSnapshot extends WebsocketStoreSnapshotType {}
export const createWebsocketStoreDefaultModel = () => types.optional(WebsocketStoreModel, {})
